import { useState, useEffect, useRef } from "react";
import {
  Loader2, Check, Upload, Trash2, RotateCcw, Camera, X, ZoomIn, FileText, Clock, CheckCircle2, XCircle, ShieldCheck,
} from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

const ANNUAL_INCOME = ["0-5L", "5-10L", "10-25L", "25-50L", "50L-1Cr", "1Cr+"];
const SOURCE_OF_FUNDS = ["salary", "business", "savings", "investments", "inheritance", "gift", "other"];
const REQUIRED_DOCS = ["pan_front", "aadhaar_front", "aadhaar_back", "selfie"];

function Field({ label, hint, error, testid, children }) {
  return (
    <div>
      {label && <label className="text-[13px] font-semibold text-cm-navy">{label}</label>}
      <div className="relative mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-[12px] text-cm-grey">{hint}</p>}
      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
    </div>
  );
}

function TextInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className={`w-full px-4 py-3 rounded-[10px] border border-[#E0E3EF] outline-none text-cm-navy text-[14px] focus:border-cm-blue transition bg-white ${props.className || ""}`}
    />
  );
}

function SelectInput({ testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className="w-full px-4 py-3 rounded-[10px] border border-[#E0E3EF] outline-none text-cm-navy text-[14px] focus:border-cm-blue transition bg-white"
    >
      {children}
    </select>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3" data-testid="kyc-wizard-error">
      {msg}
    </div>
  );
}

/* ========================== Personal Details ========================== */
function PersonalDetailsForm({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", gender: "male",
    nationality: "Indian", country_of_birth: "India", occupation: "",
    annual_income_range: "0-5L", source_of_funds: "salary",
    purpose_of_use: "", pep_declaration: false, pan_number: "",
    address: { line1: "", line2: "", city: "", state: "", country: "India", pincode: "" },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/api/kyc/personal", form);
      onDone();
    } catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="kyc-personal-form">
      <div>
        <h3 className="text-[18px] font-bold text-cm-navy">Personal details</h3>
        <p className="text-[13px] text-cm-grey mt-1">Exactly as per your government ID.</p>
      </div>

      <Field label="Full name (as per PAN)">
        <TextInput required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} testid="kyc-full-name" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of birth">
          <TextInput type="date" required value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} testid="kyc-dob" />
        </Field>
        <Field label="Gender">
          <SelectInput value={form.gender} onChange={(e) => set("gender", e.target.value)} testid="kyc-gender">
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </SelectInput>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Nationality">
          <TextInput required value={form.nationality} onChange={(e) => set("nationality", e.target.value)} testid="kyc-nationality" />
        </Field>
        <Field label="Country of birth">
          <TextInput required value={form.country_of_birth} onChange={(e) => set("country_of_birth", e.target.value)} testid="kyc-country-birth" />
        </Field>
      </div>

      <Field label="Occupation">
        <TextInput required value={form.occupation} onChange={(e) => set("occupation", e.target.value)} testid="kyc-occupation" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Annual income">
          <SelectInput value={form.annual_income_range} onChange={(e) => set("annual_income_range", e.target.value)} testid="kyc-income">
            {ANNUAL_INCOME.map((v) => <option key={v} value={v}>₹ {v}</option>)}
          </SelectInput>
        </Field>
        <Field label="Source of funds">
          <SelectInput value={form.source_of_funds} onChange={(e) => set("source_of_funds", e.target.value)} testid="kyc-funds">
            {SOURCE_OF_FUNDS.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
          </SelectInput>
        </Field>
      </div>

      <Field label="Purpose of using CoinMBX">
        <TextInput required value={form.purpose_of_use} onChange={(e) => set("purpose_of_use", e.target.value)} testid="kyc-purpose" placeholder="e.g. Long-term investment" />
      </Field>

      <Field label="PAN number (India)">
        <TextInput value={form.pan_number} onChange={(e) => set("pan_number", e.target.value.toUpperCase())} testid="kyc-pan" placeholder="ABCDE1234F" maxLength={10} />
      </Field>

      <div className="pt-2 border-t border-[#EEF1F7]">
        <div className="text-[14px] font-bold text-cm-navy mb-3">Residential address</div>
        <Field label="Address line 1">
          <TextInput required value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} testid="kyc-addr-line1" />
        </Field>
        <div className="mt-3">
          <Field label="Address line 2 (optional)">
            <TextInput value={form.address.line2} onChange={(e) => setAddr("line2", e.target.value)} testid="kyc-addr-line2" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="City">
            <TextInput required value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} testid="kyc-addr-city" />
          </Field>
          <Field label="State">
            <TextInput required value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} testid="kyc-addr-state" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Country">
            <TextInput required value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} testid="kyc-addr-country" />
          </Field>
          <Field label="Pincode">
            <TextInput required value={form.address.pincode} onChange={(e) => setAddr("pincode", e.target.value)} testid="kyc-addr-pincode" />
          </Field>
        </div>
      </div>

      <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.pep_declaration}
          onChange={(e) => set("pep_declaration", e.target.checked)}
          data-testid="kyc-pep"
          className="mt-1 w-4 h-4 rounded accent-[#022CED]"
        />
        <span className="text-[13px] text-cm-grey leading-relaxed">
          I declare that I am a Politically Exposed Person (PEP) or related to one.
          <br /><span className="text-[11px]">(Leave unchecked if not applicable.)</span>
        </span>
      </label>

      <ErrorBox msg={err} />
      <button
        type="submit"
        disabled={loading}
        data-testid="kyc-personal-submit"
        className="w-full py-3.5 rounded-[10px] bg-cm-blue hover:bg-cm-blue-deep transition text-white font-semibold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        Save & continue to documents
      </button>
    </form>
  );
}

/* ========================== Image Viewer ========================== */
function ImageViewer({ src, onClose, label }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 grid place-items-center p-6" onClick={onClose} data-testid="kyc-image-viewer">
      <button onClick={onClose} className="absolute top-5 right-5 text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 grid place-items-center" aria-label="Close">
        <X size={18} />
      </button>
      <div className="relative max-w-[92vw] max-h-[88vh] text-center">
        <img src={src} alt={label} className="max-w-full max-h-[84vh] rounded-[12px] shadow-2xl mx-auto" onClick={(e) => e.stopPropagation()} />
        <div className="mt-3 text-white/80 text-[13px]">{label}</div>
      </div>
    </div>
  );
}

/* ========================== Doc Upload Card ========================== */
function DocUploadCard({ docType, label, hint, onView, onUploaded, onRemoved, initialUploaded }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!initialUploaded);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setUploaded(!!initialUploaded); }, [initialUploaded]);
  useEffect(() => {
    if (!file) { setPreviewUrl(""); return; }
    const u = URL.createObjectURL(file);
    setPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  async function upload(f) {
    setErr(""); setUploading(true);
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", f);
      await api.post("/api/kyc/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true); onUploaded?.(docType);
    } catch (e) { setErr(getErrorMessage(e)); setUploaded(false); }
    finally { setUploading(false); }
  }

  function pick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setUploaded(false); upload(f);
  }
  function remove() {
    setFile(null); setUploaded(false); setErr("");
    onRemoved?.(docType);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      className={`rounded-[14px] border p-4 transition ${
        uploaded ? "border-emerald-300 bg-emerald-50/40"
        : err ? "border-red-300 bg-red-50/40"
        : "border-[#E0E3EF] bg-white"
      }`}
      data-testid={`kyc-doc-card-${docType}`}
    >
      <input ref={inputRef} type="file" className="hidden" accept="image/*,application/pdf" onChange={pick} />
      <div className="flex items-center gap-4">
        {previewUrl ? (
          <button type="button" onClick={() => onView?.({ src: previewUrl, label })}
                  data-testid={`kyc-doc-thumb-${docType}`}
                  className="relative shrink-0 w-[88px] h-[88px] rounded-[10px] overflow-hidden bg-cm-surface border border-[#E0E3EF] group cursor-zoom-in">
            {file?.type === "application/pdf" ? (
              <div className="w-full h-full grid place-items-center text-[10px] font-bold text-cm-grey">PDF</div>
            ) : (
              <img src={previewUrl} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center">
              <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100" />
            </div>
          </button>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()}
                  data-testid={`kyc-doc-pick-${docType}`}
                  className="shrink-0 w-[88px] h-[88px] rounded-[10px] border-2 border-dashed border-[#CBD0E0] grid place-items-center text-cm-grey hover:border-cm-blue hover:text-cm-blue transition">
            <Upload size={18} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-cm-navy truncate">
            {label}
            {uploaded && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full ml-1">
                <Check size={10} /> Uploaded
              </span>
            )}
          </div>
          <div className="text-[12px] text-cm-grey mt-0.5 truncate">
            {file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : (hint || "JPG, PNG, WEBP or PDF (max 8 MB)")}
          </div>
          <div className="mt-2.5 flex items-center gap-3">
            {file ? (
              <>
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                        className="text-[12px] text-cm-blue hover:underline font-semibold flex items-center gap-1 disabled:opacity-50"
                        data-testid={`kyc-doc-replace-${docType}`}>
                  <RotateCcw size={12} /> Replace
                </button>
                <button type="button" onClick={remove} disabled={uploading}
                        className="text-[12px] text-red-600 hover:underline font-semibold flex items-center gap-1 disabled:opacity-50"
                        data-testid={`kyc-doc-remove-${docType}`}>
                  <Trash2 size={12} /> Remove
                </button>
              </>
            ) : (
              <button type="button" onClick={() => inputRef.current?.click()}
                      className="text-[12px] text-cm-blue hover:underline font-semibold"
                      data-testid={`kyc-doc-browse-${docType}`}>
                Browse file
              </button>
            )}
            {uploading && (
              <span className="text-[11px] text-cm-grey flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Uploading…
              </span>
            )}
          </div>
          {err && <div className="mt-2 text-[12px] text-red-600">{err}</div>}
        </div>
      </div>
    </div>
  );
}

/* ========================== Selfie Capture ========================== */
function SelfieCapture({ onView, onUploaded, initialUploaded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!initialUploaded);
  const [err, setErr] = useState("");
  const [camOn, setCamOn] = useState(false);

  useEffect(() => { setUploaded(!!initialUploaded); }, [initialUploaded]);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  }
  useEffect(() => () => stopCamera(), []);

  async function openCamera() {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Camera not supported. Please upload a photo instead."); return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
      });
      streamRef.current = s; setCamOn(true);
      setTimeout(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          try { await videoRef.current.play(); } catch (_) { /* ignore */ }
        }
      }, 50);
    } catch (e) {
      setErr("Camera access denied. Please use 'Upload photo instead' below.");
    }
  }

  async function uploadBlob(blob, filename) {
    const f = new File([blob], filename, { type: blob.type || "image/jpeg" });
    setUploading(true); setErr("");
    try {
      const form = new FormData();
      form.append("doc_type", "selfie");
      form.append("file", f);
      await api.post("/api/kyc/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(true); onUploaded?.();
    } catch (e) { setErr(getErrorMessage(e)); setUploaded(false); }
    finally { setUploading(false); }
  }

  async function capture() {
    try {
      const v = videoRef.current; const c = canvasRef.current;
      if (!v || !c || !v.videoWidth) { setErr("Camera not ready, try again."); return; }
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((res, rej) => c.toBlob((b) => b ? res(b) : rej(new Error("Capture failed")), "image/jpeg", 0.92));
      setPreviewUrl(URL.createObjectURL(blob));
      stopCamera();
      await uploadBlob(blob, `selfie-${Date.now()}.jpg`);
    } catch (e) { setErr("Could not capture, try again."); }
  }

  function retake() { setUploaded(false); setPreviewUrl(""); setErr(""); openCamera(); }
  async function pickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreviewUrl(URL.createObjectURL(f));
    await uploadBlob(f, f.name);
  }

  return (
    <div className={`rounded-[14px] border p-4 transition ${
      uploaded ? "border-emerald-300 bg-emerald-50/40"
      : err ? "border-red-300 bg-red-50/40"
      : "border-[#E0E3EF] bg-white"
    }`} data-testid="kyc-selfie-card">
      <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={pickFile} />
      <canvas ref={canvasRef} className="hidden" />
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-[120px] h-[120px] rounded-[12px] overflow-hidden bg-cm-navy/95 relative">
          {camOn && !previewUrl && (
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="kyc-selfie-video" />
          )}
          {previewUrl && (
            <button type="button" onClick={() => onView?.({ src: previewUrl, label: "Live Selfie" })}
                    className="w-full h-full cursor-zoom-in relative group" data-testid="kyc-selfie-thumb">
              <img src={previewUrl} alt="Selfie" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center">
                <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
            </button>
          )}
          {!camOn && !previewUrl && (
            <div className="w-full h-full grid place-items-center text-white/70"><Camera size={24} /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-cm-navy">
            Live Selfie
            {uploaded && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full ml-1">
                <Check size={10} /> Captured
              </span>
            )}
          </div>
          <div className="text-[12px] text-cm-grey mt-0.5">Look directly at the camera in a well-lit area.</div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {!camOn && !uploaded && (
              <>
                <button type="button" onClick={openCamera}
                        className="text-[12px] bg-cm-blue text-white px-4 py-2 rounded-[8px] font-semibold flex items-center gap-1.5 hover:bg-cm-blue-deep"
                        data-testid="kyc-selfie-open">
                  <Camera size={13} /> Open camera
                </button>
                <button type="button" onClick={() => fileRef.current?.click()}
                        className="text-[12px] text-cm-blue hover:underline font-semibold"
                        data-testid="kyc-selfie-upload">
                  Upload photo instead
                </button>
              </>
            )}
            {camOn && (
              <>
                <button type="button" onClick={capture} disabled={uploading}
                        className="text-[12px] bg-cm-blue text-white px-4 py-2 rounded-[8px] font-semibold flex items-center gap-1.5 hover:bg-cm-blue-deep disabled:opacity-60"
                        data-testid="kyc-selfie-capture">
                  <Camera size={13} /> Capture
                </button>
                <button type="button" onClick={stopCamera} className="text-[12px] text-cm-grey hover:text-cm-navy font-semibold">
                  Cancel
                </button>
              </>
            )}
            {uploaded && (
              <button type="button" onClick={retake}
                      className="text-[12px] text-cm-blue hover:underline font-semibold flex items-center gap-1"
                      data-testid="kyc-selfie-retake">
                <RotateCcw size={12} /> Retake
              </button>
            )}
            {uploading && (
              <span className="text-[11px] text-cm-grey flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Uploading…
              </span>
            )}
          </div>
          {err && <div className="mt-2 text-[12px] text-red-600">{err}</div>}
        </div>
      </div>
    </div>
  );
}

/* ========================== Documents Form ========================== */
function DocumentsForm({ initialUploaded, onSubmitted, faceMatchRequired }) {
  const [viewer, setViewer] = useState(null);
  const [uploaded, setUploaded] = useState(initialUploaded || {});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceMatch, setFaceMatch] = useState(null);

  useEffect(() => {
    if (!faceMatchRequired) return;
    api.get("/api/kyc/status").then(({ data }) => {
      setFaceMatch(data.face_match || null);
    }).catch(() => {});
  }, [faceMatchRequired]);

  const allDone = REQUIRED_DOCS.every((d) => uploaded[d]);
  const faceOk = !faceMatchRequired || faceMatch?.verified;

  const markUploaded = (docType) => setUploaded((u) => ({ ...u, [docType]: true }));
  const markRemoved = (docType) => setUploaded((u) => { const n = { ...u }; delete n[docType]; return n; });

  async function runFaceMatch() {
    setFaceBusy(true);
    setSubmitErr("");
    try {
      const { data } = await api.post("/api/kyc/face-match");
      setFaceMatch(data.face_match || { verified: data.verified, message: data.message });
      if (!data.verified) setSubmitErr(data.message || "Face match failed — retake your selfie.");
    } catch (e) {
      setSubmitErr(getErrorMessage(e));
    } finally {
      setFaceBusy(false);
    }
  }

  async function handleSubmit() {
    if (!allDone || !faceOk || submitting) return;
    setSubmitErr(""); setSubmitting(true);
    try {
      await api.post("/api/kyc/submit");
      onSubmitted();
    } catch (e) { setSubmitErr(getErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="space-y-5" data-testid="kyc-docs-form">
      <div>
        <h3 className="text-[18px] font-bold text-cm-navy">Upload documents</h3>
        <p className="text-[13px] text-cm-grey mt-1">
          PAN card, Aadhaar (front & back) and a live selfie. JPG/PNG/WEBP/PDF up to 8 MB.
        </p>
      </div>

      <div className="space-y-3">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-cm-grey">PAN Card</div>
        <DocUploadCard docType="pan_front" label="PAN card" hint="Front side, clear and fully visible."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.pan_front} />

        <div className="text-[12px] font-semibold uppercase tracking-wider text-cm-grey mt-4">Aadhaar Card</div>
        <DocUploadCard docType="aadhaar_front" label="Aadhaar — front" hint="Side with photo & name."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.aadhaar_front} />
        <DocUploadCard docType="aadhaar_back" label="Aadhaar — back" hint="Side with address & QR."
                       onView={setViewer} onUploaded={markUploaded} onRemoved={markRemoved}
                       initialUploaded={uploaded.aadhaar_back} />

        <div className="text-[12px] font-semibold uppercase tracking-wider text-cm-grey mt-4">Selfie</div>
        <SelfieCapture onView={setViewer} onUploaded={() => markUploaded("selfie")} initialUploaded={uploaded.selfie} />
      </div>

      <div className="rounded-[12px] bg-cm-surface border border-[var(--cm-border)] px-4 py-3 text-[13px] text-cm-grey">
        {allDone ? (
          <span className="text-cm-navy font-medium">
            {faceMatchRequired && !faceOk
              ? "Documents uploaded — complete face verification below."
              : "All required documents uploaded. Submit for review."}
          </span>
        ) : (
          <>{REQUIRED_DOCS.filter((d) => uploaded[d]).length} of {REQUIRED_DOCS.length} required documents uploaded.</>
        )}
      </div>

      {faceMatchRequired && allDone && (
        <div className="rounded-[12px] border border-[var(--cm-border)] bg-white px-4 py-4 space-y-3">
          <div className="text-[14px] font-semibold text-cm-navy">Signzy face verification</div>
          <p className="text-[12px] text-cm-grey">Your selfie is compared to your Aadhaar photo before admin review.</p>
          {faceMatch?.verified ? (
            <p className="text-[13px] text-emerald-700 font-medium flex items-center gap-1">
              <Check size={14} /> Verified
              {faceMatch.match_percentage != null && ` (${faceMatch.match_percentage}%)`}
            </p>
          ) : (
            <>
              {faceMatch?.message && <p className="text-[12px] text-red-600">{faceMatch.message}</p>}
              <button type="button" onClick={runFaceMatch} disabled={faceBusy}
                className="text-[13px] bg-cm-navy text-white px-4 py-2.5 rounded-[8px] font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                {faceBusy && <Loader2 size={14} className="animate-spin" />}
                Run face verification
              </button>
            </>
          )}
        </div>
      )}

      {submitErr && (
        <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
          {submitErr}
        </div>
      )}

      <button type="button" onClick={handleSubmit} disabled={!allDone || !faceOk || submitting}
              data-testid="kyc-docs-submit"
              className="w-full py-3.5 rounded-[10px] bg-cm-blue hover:bg-cm-blue-deep transition text-white font-semibold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? "Submitting…" : "Submit for review"}
      </button>

      <ImageViewer src={viewer?.src} label={viewer?.label} onClose={() => setViewer(null)} />
    </div>
  );
}

/* ========================== Status Banner ========================== */
function StatusBanner({ status, reason }) {
  const map = {
    approved: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "KYC Approved — you can start trading." },
    pending_review: { c: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, label: "KYC under review — usually takes 24–48 hours." },
    rejected: { c: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: `KYC rejected: ${reason || "please re-upload your documents"}` },
    personal_done: { c: "bg-blue-50 text-cm-blue border-blue-200", Icon: ShieldCheck, label: "Personal details saved. Now upload your documents." },
    not_started: { c: "bg-blue-50 text-cm-blue border-blue-200", Icon: ShieldCheck, label: "Complete KYC to unlock trading." },
  };
  const { c, Icon, label } = map[status] || map.not_started;
  return (
    <div className={`flex items-start gap-3 rounded-[14px] border px-5 py-4 ${c}`} data-testid="kyc-banner">
      <Icon size={18} className="mt-0.5" />
      <div className="flex-1 text-[14px] font-medium">{label}</div>
    </div>
  );
}

/* ========================== Main Wizard ========================== */
export default function KycWizard({ faceMatchRequired = false }) {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | personal | docs | review | approved | rejected

  async function load() {
    try {
      const { data: d } = await api.get("/api/user/overview");
      setData(d);
      const status = d.user?.kyc_status || "not_started";
      if (status === "approved") setPhase("approved");
      else if (status === "pending_review") setPhase("review");
      else if (status === "rejected") setPhase("rejected");
      else if (status === "personal_done") setPhase("docs");
      else setPhase("personal");
    } catch (e) {
      setPhase("personal");
    }
  }

  useEffect(() => { load(); }, []);

  const initialDocs = (() => {
    const map = {};
    for (const d of (data?.documents || [])) map[d.doc_type] = true;
    return map;
  })();

  if (phase === "loading") {
    return (
      <div className="text-cm-grey text-[13px] inline-flex items-center gap-2" data-testid="kyc-loading">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kyc-wizard">
      <StatusBanner status={data?.user?.kyc_status || "not_started"} reason={data?.user?.kyc_rejection_reason} />

      {phase === "personal" && (
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
          <PersonalDetailsForm onDone={() => load()} />
        </div>
      )}

      {phase === "docs" && (
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
          <DocumentsForm initialUploaded={initialDocs} onSubmitted={() => load()} faceMatchRequired={faceMatchRequired} />
        </div>
      )}

      {(phase === "review" || phase === "approved" || phase === "rejected") && (
        <>
          {data?.kyc_profile && (
            <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
              <h3 className="text-[15px] font-bold text-cm-navy mb-4">Personal details</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
                {[
                  ["Full name", data.kyc_profile.full_name],
                  ["Date of birth", data.kyc_profile.date_of_birth],
                  ["Gender", data.kyc_profile.gender],
                  ["Nationality", data.kyc_profile.nationality],
                  ["Occupation", data.kyc_profile.occupation],
                  ["Annual income", data.kyc_profile.annual_income_range],
                  ["Source of funds", data.kyc_profile.source_of_funds],
                  ["PAN", data.kyc_profile.pan_number || "—"],
                  ["PEP", data.kyc_profile.pep_declaration ? "Yes" : "No"],
                  ["City", data.kyc_profile.address?.city],
                  ["State", data.kyc_profile.address?.state],
                  ["Pincode", data.kyc_profile.address?.pincode],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-cm-grey text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-cm-navy font-medium">{v || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
            <h3 className="text-[15px] font-bold text-cm-navy mb-4">Documents</h3>
            {(data?.documents || []).length === 0 ? (
              <div className="text-cm-grey text-[13px]">No documents uploaded yet.</div>
            ) : (
              <ul className="divide-y divide-[var(--cm-border)]">
                {data.documents.map((d) => (
                  <li key={d.doc_type} className="flex items-center gap-3 py-3" data-testid={`kyc-doc-${d.doc_type}`}>
                    <FileText size={16} className="text-cm-blue shrink-0" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-cm-navy capitalize">{d.doc_type.replace(/_/g, " ")}</div>
                      <div className="text-[11px] text-cm-grey">{d.file_name}</div>
                    </div>
                    <span className="text-[11px] text-emerald-600 font-semibold">Uploaded</span>
                  </li>
                ))}
              </ul>
            )}

            {phase === "rejected" && (
              <button type="button"
                      onClick={async () => { await api.post("/api/user/kyc/resubmit"); load(); }}
                      data-testid="kyc-resubmit"
                      className="mt-5 inline-flex items-center px-5 py-2.5 rounded-[10px] bg-cm-blue text-white font-semibold text-[13px] hover:bg-cm-blue-deep">
                Re-upload documents
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
